/* eslint-disable */
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { AIPromptRepository } from '@src/repositories';
import type { TranscriptionStore } from '@src/stores-v2';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import { logChatGptOutput } from '@src/utility/datadog';
import axios from 'axios';
import TurndownService from 'turndown';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';

import type { AIPrompt } from '../types.ts/ai_prompts';
import type { TalkingPointContent } from '../types.ts/general';
import type {
  ContentViewData,
  PunchListItem,
  SupportedGeneratedContentTypes,
} from '../types.ts/story';
import type { FrameAnalysisResult } from '../transcript/utils/frameExtraction';
import { LogManager } from '@src/utility/Logger';
import {
  buildOverlappingChunks,
  buildRosterTextForAnchors,
  extractSpeakerSegments,
} from '../utility/speakerIdentification';

const turndownService = new TurndownService();

type GenerateStreamResponseParams = {
  key: SupportedGeneratedContentTypes;
  template?: AIPrompt;
  version?: '1' | '2';
  type?: 'existing' | 'new' | 'regenerate';
  customInstructions?: string;
  revision?: string;
  brandVoice?: string;
  avoidWords?: string;
  // For speaker filtering
  selectedSpeakers?: Speaker[];
  // For goal targeting
  selectedGoal?: GoalOption | null;
  // For audience targeting
  selectedAudiences?: AudienceOption[];
  selectedLanguage?: string | null;
};

export default class ChatGPTService {
  protected videoCreator: IVideoCreatorStore;

  protected transcriptionStore: TranscriptionStore;

  protected aiPromptRepository: AIPromptRepository;

  public streamProcessDone = false;

  private controller: AbortController | null = null;

  public talkingPointController: AbortController | null = null;

  private logger = LogManager.getLogger('service:ChatGPTService');

  constructor(
    videoCreator: IVideoCreatorStore,
    aiPromptRepository: AIPromptRepository,
  ) {
    this.videoCreator = videoCreator;
    this.transcriptionStore = videoCreator.rootStore.transcriptionStore;
    this.aiPromptRepository = aiPromptRepository;
  }

  fetchAiPrompt = async (key: string) => {
    const prompt = await this.aiPromptRepository.getPromptByTitle(key);
    return prompt;
  };

  private getExistingResponseData = (key: SupportedGeneratedContentTypes) =>
    this.videoCreator.contentStudioGeneratedContent?.[key]?.content?.response ||
    '';

  public getTranscript = (
    key: SupportedGeneratedContentTypes,
    selectedSpeakers?: Speaker[],
  ): string | undefined => {
    try {
      let elements;

      // Get the right elements based on content type
      if (key === 'Music Selection' || key === 'Photo Punchlist') {
        elements = this.transcriptionStore
          .getFinalTranscriptionElements()
          ?.filter((el: any) => el.state !== 'removed' && el.state !== 'cut');
      } else {
        elements = this.transcriptionStore.originalTranscription?.elements;
      }

      // Safety check - if no elements, return early
      if (!elements || elements.length === 0) {
        this.logger.warn('No transcript elements found for key:', key);
        return '';
      }
      // Apply speaker filtering if specified
      if (selectedSpeakers && selectedSpeakers.length > 0) {
        // Filter elements to include only those from selected speakers
        const filteredElements = elements.filter(
          (el: any) =>
            el.speaker_label &&
            selectedSpeakers?.some(s => s.speakerLabel === el.speaker_label),
        );

        // If filtering removed all elements, warn but continue with all elements
        if (filteredElements.length === 0) {
          this.logger.warn(
            'Speaker filtering removed all elements. Using all transcript elements.',
          );
        } else {
          elements = filteredElements;
        }
      }

      // Format transcript with speaker information if filtering is applied
      if (
        (key === 'Content Writer | Generate Speaker Bio' ||
          (selectedSpeakers && selectedSpeakers.length > 0)) &&
        elements.length > 0
      ) {
        // Group contiguous elements by speaker
        // with a special prefix for each speaker
        // and a new line between each speaker
        let formattedText = '';
        let currentSpeaker = '';
        let currentText = '';

        // Process each element
        elements.forEach((element: any, index: number) => {
          if (!element || !element.speaker_label) return; // Skip null/undefined elements

          const speakerLabel = element.speaker_label;
          const elementText = element.value || '';

          // Skip empty text elements
          if (!elementText.trim()) return;

          // If speaker changed, add the previous speaker's text and start new speaker
          if (speakerLabel !== currentSpeaker) {
            // Add the previous speaker's text if there was one
            if (currentSpeaker && currentText) {
              const speakerName =
                selectedSpeakers?.find(s => s.speakerLabel === currentSpeaker)
                  ?.name || currentSpeaker;
              formattedText += `${speakerName}: ${currentText}\n\n`;
            }

            // Start tracking the new speaker
            currentSpeaker = speakerLabel;
            currentText = elementText;
          } else {
            // Continue with the same speaker, append text
            currentText += ` ${elementText}`;
          }

          // Handle the last element
          if (index === elements.length - 1 && currentText) {
            const speakerName =
              selectedSpeakers?.find(s => s.speakerLabel === currentSpeaker)
                ?.name || currentSpeaker;
            formattedText += `${speakerName}: ${currentText}`;
          }
        });

        this.logger.info(
          'Using formatted transcript with speakers',
          formattedText,
        );

        // Fail-safe check for empty formatted text
        if (formattedText && formattedText.trim().length > 0) {
          return formattedText.trim();
        }
        this.logger.warn(
          'Formatted text is empty, falling back to default formatting',
        );
      }

      // Default formatting - just join all text
      this.logger.info('Using default transcript formatting');
      const defaultText = elements
        .map((e: any) => e?.value || '')
        .join('')
        .trim();

      // Final safety check
      if (!defaultText) {
        this.logger.warn('Default transcript formatting produced empty text');
        return 'No transcript text available';
      }

      return defaultText;
    } catch (error) {
      this.logger.error('Error in getTranscript method:', error);
      return 'Error processing transcript';
    }
  };

  private async getResponseInputData(params: GenerateStreamResponseParams) {
    const {
      key,
      template,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
      selectedGoal,
      selectedAudiences,
      selectedLanguage: selectedLanguageCode,
    } = params;
    const transcript = this.getTranscript(key, selectedSpeakers);
    let aiPrompt = template;
    if (!aiPrompt && customInstructions) {
      aiPrompt = await this.fetchAiPrompt(`Custom ${key}`);
    }
    if (!aiPrompt) {
      aiPrompt = await this.fetchAiPrompt(key);
    }

    if (!aiPrompt || !transcript || !this.videoCreator.story?.id) return;
    let existingResponse: any =
      type === 'new' ? '' : this.getExistingResponseData(key);
    if (version === '2') {
      existingResponse = { ...aiPrompt, response: existingResponse.response };
    }

    let systemMessage = aiPrompt.description;
    if (customInstructions) {
      if (template) {
        systemMessage += `\n\nAdditional customization: ${customInstructions}\n\nIf the above customization contains keywords or themes, focus the content on those topics. If it contains specific instructions or directives, follow those instructions while maintaining the theme's style and format.`;
      } else {
        systemMessage += `\n\n${customInstructions}`;
      }
    }

    let followUp = '';
    if (type === 'regenerate') followUp += aiPrompt.followUp;
    if (revision) followUp += `${followUp ? '\n\n' : ''}${revision}`;

    // Add goal context if a goal is selected
    if (
      selectedGoal &&
      selectedGoal.id !== 'none' &&
      selectedGoal.finalPrompt
    ) {
      const goalPrompt = await this.fetchAiPrompt(
        'Content Writing | Context | Goal',
      );
      if (goalPrompt) {
        systemMessage = `\n\n${systemMessage}\n\n${goalPrompt.description}\n\n${selectedGoal.label}: ${selectedGoal.finalPrompt}`;
      }
    }

    // Add audience context if audiences are selected
    if (selectedAudiences && selectedAudiences.length > 0) {
      const audiencePrompt = await this.fetchAiPrompt(
        'Content Writing | Context | Audiences',
      );
      if (audiencePrompt) {
        // Concatenate all audience prompts
        const combinedAudiencePrompts = selectedAudiences
          .map(audience => `${audience.label}: ${audience.finalPrompt}`)
          .join('\n\n');
        systemMessage = `\n\n${systemMessage}\n\n${audiencePrompt.description}\n\n${combinedAudiencePrompts}`;
      }
    }

    // Always add formatting instruction for quotes
    if (key === 'Quotes') {
    }

    // Add speaker context to the prompt if filtering is applied
    if (selectedSpeakers && selectedSpeakers.length > 0) {
      // Find speaker labels from transcript
      const allElements =
        this.transcriptionStore.originalTranscription?.elements || [];
      const allSpeakerLabels = Array.from(
        new Set(
          allElements
            .filter((el: any) => el.speaker_label)
            .map((el: any) => el.speaker_label),
        ),
      );

      // Only add filtering instructions if not selecting all speakers
      if (selectedSpeakers.length < allSpeakerLabels.length) {
        // Format speaker names for display in prompt
        const speakerNames = selectedSpeakers
          .map(s => s.name || `Speaker ${s.speakerLabel.replace('spk_', '')}`)
          .join(', ');

        // Base speaker context
        let speakerContext = '';

        const speakerTitlePrompt = await this.fetchAiPrompt('Speaker Title');
        if (speakerTitlePrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerTitlePrompt.description
          }\nSpeaker titles: \n`;
          selectedSpeakers.forEach(speaker => {
            if (speaker.title) {
              systemMessage += `${speaker.name}: ${speaker.title}\n`;
            }
          });
        }

        const speakerBioPrompt = await this.fetchAiPrompt('Speaker Bio');
        if (speakerBioPrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerBioPrompt.description
          }\nSpeaker bios: \n`;
          selectedSpeakers.forEach(speaker => {
            if (speaker.bio) {
              systemMessage += `${speaker.name}: ${speaker.bio}\n`;
            }
          });
        }

        // Customize speaker instructions based on content type
        switch (key) {
          case 'Quotes':
            const formattingInstruction = `IMPORTANT: End each quote with the speaker's name in the format " --- Speaker Name". For example: "This is an important quote. --- John Smith". Every quote MUST be formatted this way with exactly 3 dashes to ensure proper attribution.`;
            systemMessage += `${
              systemMessage ? '\n\n' : ''
            }${formattingInstruction}`;
            speakerContext = `Please generate quotes ONLY using the words spoken by ${speakerNames}. Ignore content from other speakers.`;
            break;

          case 'Blog':
            speakerContext = `Focus the blog content primarily on perspectives and insights from ${speakerNames}. While you may reference other speakers for context, make ${speakerNames} the central voice(s) of the blog. When including direct quotes, attribute them properly to the speaker.`;
            break;

          case 'Social Post':
            speakerContext = `Create social posts that highlight key points made by ${speakerNames}. If directly quoting, include attribution to the speaker. Focus on the most engaging and shareable insights from these specific speakers.`;
            break;

          case 'Email':
            speakerContext = `Frame the email content around insights from ${speakerNames}, using their perspectives as the main value proposition while maintaining a professional email tone. Include relevant quotes with proper attribution when appropriate.`;
            break;

          // Add default case for other content types
          default:
            speakerContext = `Focus on content from ${speakerNames} when generating your response. Prioritize their perspectives and insights over other speakers in the transcript.`;
        }

        // Add the speaker context to the prompt
        systemMessage += `\n\n${speakerContext}`;

        // Add voice descriptions if available for selected speakers
        const speakersWithVoice = selectedSpeakers.filter(
          s => s.voice && s.voice.trim().length > 0,
        );

        if (speakersWithVoice.length > 0) {
          let voiceContext =
            '\n\nWhen generating content, match these speaking styles:';

          speakersWithVoice.forEach(speaker => {
            const speakerName =
              speaker.name ||
              `Speaker ${speaker.speakerLabel.replace('spk_', '')}`;
            voiceContext += `\n- ${speakerName}: ${speaker.voice}`;
          });

          // Add specific instructions based on content type
          switch (key) {
            case 'Quotes':
              voiceContext += `\n\nUse these voice descriptions to select quotes that best represent each speaker's unique voice and style.`;
              break;

            case 'Blog':
              voiceContext += `\n\nWhen crafting the blog, ensure the overall tone reflects these voice characteristics, especially when paraphrasing their perspectives.`;
              break;

            case 'Social Post':
              voiceContext += `\n\nMatch these voice characteristics when creating social posts to maintain authenticity.`;
              break;

            case 'Email':
              voiceContext += `\n\nWhen writing the email, incorporate these voice characteristics into the messaging while maintaining professionalism.`;
              break;
          }

          // Add the voice context to the prompt
          systemMessage += voiceContext;
        }
      }
    }

    if (brandVoice) {
      const brandVoicePrompt = await this.fetchAiPrompt('Brand Voice');
      if (brandVoicePrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          brandVoicePrompt.description
        }\nBrand voice: ${brandVoice}`;
      }
    }

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }\nAvoid words: ${avoidWords}`;
      }
    }

    // Use selected language
    if (selectedLanguageCode) {
      const languageName = ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
        lang => lang.value === selectedLanguageCode,
      )?.caption;
      if (languageName) {
        systemMessage += `${
          systemMessage ? '\n\n' : ''
        }${`Generate the content in the following language: ${languageName}`}`;
      }
    }

    return {
      systemMessage,
      transcript,
      existingResponse,
      followUp,
    };
  }

  private updateGeneratedContent = (
    key: SupportedGeneratedContentTypes,
    response: string = '',
  ) => {
    const generatedContent = this.videoCreator.contentStudioGeneratedContent;
    this.videoCreator.contentStudioGeneratedContent = {
      ...(generatedContent || {}),
      [key]: {
        ...(generatedContent?.[key] || {}),
        content: {
          ...(generatedContent?.[key]?.content || {}),
          response,
        },
      },
    } as ContentViewData;
  };

  public async regenerateStreamResponse(
    params: GenerateStreamResponseParams & {
      useSearch?: boolean;
      setLoading: (e: boolean) => void;
      responseFormat?: 'speaker_id';
      useBetterReasoning?: boolean;
    },
  ) {
    const {
      key,
      template,
      setLoading,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
      selectedGoal,
      selectedAudiences,
      useSearch,
      selectedLanguage,
      responseFormat,
      useBetterReasoning,
    } = params;
    try {
      // console.log('Starting regenerateStreamResponse with params:', {
      //   key,
      //   template: template?.title,
      //   hasSelectedSpeakers: !!selectedSpeakers,
      //   selectedSpeakerCount: selectedSpeakers?.length || 0,
      //   selectedSpeakers: selectedSpeakers?.map((s) => s.speakerLabel),
      //   type,
      // });

      // Cancel any in-progress requests
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }

      // Create a new abort controller
      this.controller = new AbortController();
      const { signal } = this.controller;

      // Prepare the request data
      const items = await this.getResponseInputData({
        key,
        template,
        version,
        type,
        customInstructions,
        revision,
        brandVoice,
        avoidWords,
        selectedSpeakers,
        selectedGoal,
        selectedAudiences,
        selectedLanguage,
      });

      // Check if we have all the required data
      if (!items) {
        console.error(
          'getResponseInputData returned null or undefined. API request aborted.',
        );
        setLoading(false);
        return;
      }

      const { systemMessage, transcript, existingResponse, followUp } = items;
      const storyId = this.videoCreator.story?.id;

      // Validate required data
      if (!storyId) {
        console.error('No story ID available. API request aborted.');
        setLoading(false);
        return;
      }

      if (!transcript || transcript.trim().length === 0) {
        console.error('No transcript text available. API request aborted.');
        setLoading(false);
        return;
      }

      // console.log('Got response input data:', {
      //   hasSystemMessage: !!systemMessage,
      //   hasTranscript: !!transcript,
      //   transcriptLength: transcript?.length || 0,
      //   hasExistingResponse: !!existingResponse,
      //   hasFollowUp: !!followUp,
      //   storyId,
      // });

      // Construct API URL
      const apiUrl = `${
        process.env.REACT_APP_API_URL
      }/api/aiprompts/regenerate-gpt-response-stream${
        version === '2' ? '-v2' : ''
      }`;
      // console.log('Calling API at:', apiUrl);

      // Prepare request body
      const requestBody = {
        systemMessage,
        transcript,
        existingResponse,
        followUp,
        storyId,
        useSearch,
        responseFormat,
        useBetterReasoning,
      };

      try {
        // Clear existing content before making the request
        this.updateGeneratedContent(key, '');

        // Make API request
        const res = await fetch(apiUrl, {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        // Handle error responses
        if (!res.ok) {
          console.error(
            'API response not OK. Status:',
            res.status,
            res.statusText,
          );
          try {
            const errorText = await res.text();
            console.error('Error response:', errorText);

            // Update content with error message for user
            this.updateGeneratedContent(
              key,
              `Error generating content: ${res.status} ${res.statusText}`,
            );
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          setLoading(false);
          return;
        }

        // Process the response stream
        const data = res.body;
        if (!data) {
          console.error('Response body is null or undefined');
          this.updateGeneratedContent(key, 'Error: No response data received');
          setLoading(false);
          return;
        }

        // Set up stream processing
        const reader = data.getReader();
        const decoder = new TextDecoder();

        // Mark content as generated
        if (this.videoCreator.contentStudioGeneratedContent?.[key]) {
          this.videoCreator.contentStudioGeneratedContent[
            key
          ].hasBeenGenerated = true;
          this.videoCreator.contentStudioGeneratedContent[key].usedSpeakers =
            !!selectedSpeakers?.length;
          this.videoCreator.contentStudioGeneratedContent[
            key
          ].selectedLanguage = selectedLanguage;
        }

        // Process the stream
        await this.processGeneratedContentByChunk(
          reader,
          decoder,
          setLoading,
          key,
          key, // Use the key as the logging segment
        );
      } catch (fetchError: any) {
        // Handle fetch errors
        console.error('Fetch error:', fetchError.message);
        this.updateGeneratedContent(key, `Error: ${fetchError.message}`);
        setLoading(false);
      }
    } catch (err: any) {
      // Handle top-level errors
      if (err.name === 'AbortError') {
        console.log('Request aborted', err.message);
      } else {
        console.error('Error occurred:', err);
        // Display error to user
        this.updateGeneratedContent(key, `An error occurred: ${err.message}`);
      }
      setLoading(false);
    }
  }

  public async matchPunchListWithSources(
    punchList: PunchListItem[],
    sources: string[],
  ): Promise<{ punchList: PunchListItem[] }> {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/match-punchlist-with-sources`,
      { punchList, sources, storyId: this.videoCreator.story?.id },
    );
    return data;
  }

  public async generatePunchListItem(line: string) {
    const storyId = this.videoCreator.story?.id;
    if (!line || !storyId) return;

    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/generate-punchlist-item`,
      { line, storyId },
    );
    return data;
  }

  private async processGeneratedContentByChunk(
    reader: any,
    decoder: TextDecoder,
    setLoading: (e: boolean) => void,
    key: SupportedGeneratedContentTypes,
    loggingKey: string,
  ) {
    let i = 0;
    while (!this.streamProcessDone) {
      const { value, done: doneReading } = await reader.read();
      this.streamProcessDone = doneReading;
      let chunk = decoder.decode(value);
      let addition = '';

      if (key === 'Email') {
        if (i === 0 && !chunk.includes('##')) addition = '## ';
        if (chunk === ' ') chunk = '\n\n';
        if (chunk.includes('Subject:')) {
          chunk = chunk.replace('Subject:', '').trim();
        }
      } else if (key === 'Blog') {
        this.videoCreator.selectedGeneratedContent.blog = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Social Post') {
        this.videoCreator.selectedGeneratedContent.socialPost = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Quotes') {
        // Ensure each quote has the proper formatting if it doesn't already
        if (doneReading) {
          // Access the full text after processing is complete
          const existingResponse = this.getExistingResponseData(key);
          // console.log('Existing response:', existingResponse);
        }
      }

      const existingResponse = this.getExistingResponseData(key);
      const text = existingResponse + addition + chunk;
      this.updateGeneratedContent(key, text);
      if (doneReading) {
        logChatGptOutput({ [loggingKey]: text });
        setLoading(false);
      }
      i++;
    }
    if (this.streamProcessDone) {
      this.controller = null;
      this.streamProcessDone = false;
    }
  }

  async generateTalkingPoint({
    setLoading,
    brandText,
    key = null,
    template,
    avoidWords,
  }: {
    setLoading: (e: boolean) => void;
    brandText?: string;
    key?: (keyof TalkingPointContent & 'all') | null;
    template?: AIPrompt | null;
    avoidWords?: string;
  }) {
    try {
      if (this.talkingPointController) {
        this.talkingPointController.abort();
      }
      this.talkingPointController = new AbortController();
      const { signal } = this.talkingPointController;

      const transcript = this.transcriptionStore
        .getFinalTranscriptionElements()
        ?.map(e => e.value || '')
        .join('');

      let prompts = template;
      if (!prompts) {
        prompts = await this.fetchAiPrompt('Fundraising prompt');
      }
      if (!prompts?.promptFields || !transcript) return;

      const model = 'gpt-4o';
      const temperature = 1;

      if (!key || key === 'all') {
        if (!key || !this.videoCreator.talkingPointContent) {
          this.videoCreator.talkingPointContent = {} as TalkingPointContent;
        } else {
          this.videoCreator.talkingPointContent = Object.fromEntries(
            Object.entries(this.videoCreator.talkingPointContent).map(
              ([key, value]) => [key, { prompt: value.prompt, content: '' }],
            ),
          ) as TalkingPointContent;
        }

        for (const prompt of prompts.promptFields) {
          await this.runTalkingPointAi(
            transcript,
            prompts.description,
            setLoading,
            prompt,
            temperature,
            model,
            key,
            brandText,
            avoidWords,
            signal,
          );
        }
      } else {
        const prompt = prompts.promptFields.find(f => f.name === key);
        if (!prompt) return;

        await this.runTalkingPointAi(
          transcript,
          prompts.description,
          setLoading,
          prompt,
          temperature,
          model,
          key,
          brandText,
          avoidWords,
          signal,
        );
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request aborted', error, error.message);
      } else {
        console.error('Error occurred:', error);
      }
    }
  }

  getPrevContent(content: TalkingPointContent | null, promptName: string) {
    try {
      if (content && promptName in content) {
        return content[promptName as keyof TalkingPointContent];
      }
      return { prompt: '', content: '' };
    } catch (error) {
      console.log('Error: ', error);
    }
  }

  async runTalkingPointAi(
    transcript: string,
    description: string,
    setLoading: (e: boolean) => void,
    prompt: AIPrompt['promptFields'][0],
    temperature: number,
    model: string,
    key: (keyof TalkingPointContent & 'all') | null,
    brandText: string | undefined,
    avoidWords: string | undefined,
    signal: AbortSignal,
  ) {
    let prevContent = '';
    let prevPrompt = '';
    if (key) {
      const prevData = this.getPrevContent(
        this.videoCreator.talkingPointContent,
        prompt.name,
      );
      prevContent = turndownService.turndown(prevData?.content ?? '');
      prevPrompt = prevData?.prompt ?? '';
    }

    const promptDescription = `${prompt.description}.  Return only markdown with no other comments.`;
    let currentPrompt = `${promptDescription}`;

    if (brandText) currentPrompt += ` ${brandText}`;

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        currentPrompt += `${currentPrompt ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }: ${avoidWords}`;
      }
    }

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-talking-point-response`,
      {
        signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript,
          systemMessage: description,
          prevPrompt: prevPrompt || promptDescription,
          currentPrompt,
          temperature,
          model,
          prevContent,
        }),
      },
    );

    const data = res.body;
    if (!data) return;
    const reader = data.getReader();
    const decoder = new TextDecoder();
    let done = false;

    if (key && this.videoCreator.talkingPointContent?.hasOwnProperty(key)) {
      this.videoCreator.talkingPointContent[
        key as keyof TalkingPointContent
      ].content = '';
    }

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      const chunk = decoder.decode(value);
      setLoading(false);

      const key = prompt.name as keyof TalkingPointContent;
      const existingContent = (this.videoCreator.talkingPointContent ||
        {}) as TalkingPointContent;

      const content = (existingContent[key]?.content || '') + chunk;
      this.videoCreator.talkingPointContent = {
        ...(existingContent as TalkingPointContent),
        [key]: {
          content,
          prompt: currentPrompt,
        },
      };
      if (done) {
        logChatGptOutput({ 'stream-talking-point-response': content });
      }
    }
  }

  regenerateArtifactTitle = async ({
    artifactId,
    storyId,
    organizationId,
  }: {
    artifactId: string;
    storyId: string | undefined;
    organizationId: string | undefined;
  }) => {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/regenerate-artifact-title`,
      {
        artifactId,
        storyId,
        organizationId,
      },
    );
    return data;
  };

  public async generateSpeakerBio({
    speakerName,
    speakerRole,
    selectedSpeakers,
    customInstructions,
    existingBio,
  }: {
    speakerName: string;
    speakerRole?: string;
    selectedSpeakers?: Speaker[];
    customInstructions?: string;
    existingBio?: string;
  }): Promise<string> {
    // Create instructions based on whether this is initial generation or regeneration
    let instructions = `Focus on generating a bio for ${speakerName}${
      speakerRole ? `, ${speakerRole}` : ''
    }.`;

    if (customInstructions && existingBio) {
      // For regeneration, include the user's specific instructions
      instructions += `\n\nThe user has requested the following changes to the existing bio: ${customInstructions}`;
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedBio = '';
      let isFirstChunk = true;

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Speaker Bio') {
          generatedBio = response;
        }
        originalUpdate(key, response);
      };

      // Store existing bio in content studio if regenerating
      if (existingBio) {
        this.updateGeneratedContent(
          'Content Writer | Generate Speaker Bio',
          existingBio,
        );
      }

      // Use the existing regenerateStreamResponse method
      // The system will:
      // 1. Fetch the "Content Writer | Generate Speaker Bio" prompt from AI prompts
      // 2. Use its description as the base system message
      // 3. Append our customInstructions to personalize for the specific speaker
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Speaker Bio',
        type: existingBio ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingBio ? customInstructions : undefined,
        selectedSpeakers, // Pass speakers for proper transcript formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedBio) {
            // Restore original method
            this.updateGeneratedContent = originalUpdate;
            resolve(generatedBio);
          }
        },
      }).catch(error => {
        // Restore original method
        this.updateGeneratedContent = originalUpdate;
        reject(error);
      });
    });
  }

  public async generateSpeakerVoice({
    speakerName,
    speakerRole,
    selectedSpeakers,
    customInstructions,
    existingVoice,
  }: {
    speakerName: string;
    speakerRole?: string;
    selectedSpeakers?: Speaker[];
    customInstructions?: string;
    existingVoice?: string;
  }): Promise<string> {
    // Create instructions based on whether this is initial generation or regeneration
    let instructions = `Focus on generating a voice description for ${speakerName}${
      speakerRole ? `, ${speakerRole}` : ''
    }.`;

    if (customInstructions && existingVoice) {
      // For regeneration, include the user's specific instructions
      instructions += `\n\nThe user has requested the following changes to the existing voice description: ${customInstructions}`;
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedVoice = '';
      let isFirstChunk = true;

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Speaker Voice') {
          generatedVoice = response;
        }
        originalUpdate(key, response);
      };

      // Store existing voice in content studio if regenerating
      if (existingVoice) {
        this.updateGeneratedContent(
          'Content Writer | Generate Speaker Voice',
          existingVoice,
        );
      }

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Speaker Voice',
        type: existingVoice ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingVoice ? customInstructions : undefined,
        selectedSpeakers, // Pass speakers for proper transcript formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedVoice) {
            // Restore original method
            this.updateGeneratedContent = originalUpdate;
            resolve(generatedVoice);
          }
        },
      }).catch(error => {
        // Restore original method
        this.updateGeneratedContent = originalUpdate;
        reject(error);
      });
    });
  }

  private getBrandKitData() {
    const branding = this.videoCreator.organization?.branding;
    if (!branding) return null;

    return {
      brandVoice: branding.brandVoice || '',
      avoidWords: branding.avoidWords || '',
      missionStatement: branding.missionStatement || '',
      keyMetrics: branding.keyMetrics || '',
      customVocabulary: branding.customVocabulary || '',
      organizationWebsite: branding.organizationWebsite || '',
      externalLinks: branding.externalLinks || '',
      primaryColors: branding.primaryColors || '',
      secondaryColors: branding.secondaryColors || '',
      // Existing goals and audiences for context
      existingGoals:
        branding.goals?.map(g => `${g.label}: ${g.finalPrompt}`).join('\n') ||
        '',
      existingAudiences:
        branding.audiences
          ?.map(a => `${a.label}: ${a.finalPrompt}`)
          .join('\n') || '',
    };
  }

  public async generateAudience({
    audienceName,
    customInstructions,
    existingAudience,
  }: {
    audienceName?: string;
    customInstructions?: string;
    existingAudience?: string;
  }): Promise<{ title?: string; description: string } | string> {
    const generateTitle = !existingAudience; // Only generate title for new audiences

    // Create instructions based on whether this is initial generation or regeneration
    let instructions = '';

    if (generateTitle) {
      // For new audiences, ask for both title and description
      instructions = audienceName
        ? `Generate a target audience based on: "${audienceName}". Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this audience segment (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the audience`
        : 'Generate a target audience. Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this audience segment (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the audience';
    } else {
      // For regeneration, only update the description
      instructions = audienceName
        ? `Focus on generating an audience description for "${audienceName}".`
        : 'Generate a target audience description based on the brand context.';

      if (customInstructions && existingAudience) {
        // For regeneration, include the user's specific instructions
        instructions += `\n\nThe user has requested the following changes to the existing audience description: ${customInstructions}`;
      }
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedAudience = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Audience') {
          generatedAudience = response;
        }
        originalUpdate(key, response);
      };

      // Store existing audience in content studio if regenerating
      if (existingAudience) {
        this.updateGeneratedContent(
          'Content Writer | Generate Audience',
          existingAudience,
        );
      }

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.missionStatement) {
          brandContext += `Mission Statement: ${brandKitData.missionStatement}\n`;
        }
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.keyMetrics) {
          brandContext += `Key Metrics: ${brandKitData.keyMetrics}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
        if (brandKitData.existingAudiences) {
          brandContext += `\nOther Defined Audiences:\n${brandKitData.existingAudiences}\n`;
        }
        if (brandKitData.existingGoals) {
          brandContext += `\nCompany Goals:\n${brandKitData.existingGoals}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Audience',
        type: existingAudience ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingAudience
            ? customInstructions
            : undefined,
        setLoading: (loading: boolean) => {
          if (!loading && generatedAudience) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            // Try to parse JSON response for new audiences
            if (generateTitle) {
              try {
                const parsed = JSON.parse(generatedAudience);
                if (parsed.title && parsed.description) {
                  resolve(parsed);
                } else {
                  resolve({ description: generatedAudience });
                }
              } catch {
                resolve({ description: generatedAudience });
              }
            } else {
              resolve(generatedAudience);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateGoal({
    goalName,
    customInstructions,
    existingGoal,
  }: {
    goalName?: string;
    customInstructions?: string;
    existingGoal?: string;
  }): Promise<{ title?: string; description: string } | string> {
    const generateTitle = !existingGoal; // Only generate title for new goals

    // Create instructions based on whether this is initial generation or regeneration
    let instructions = '';

    if (generateTitle) {
      // For new goals, ask for both title and description
      instructions = goalName
        ? `Generate a business goal based on: "${goalName}". Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this goal (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the goal with strategies and metrics`
        : 'Generate a business goal. Return a JSON object with two fields:\n1. "title": A concise, descriptive title for this goal (2-5 words)\n2. "description": A detailed 2-3 paragraph description of the goal with strategies and metrics';
    } else {
      // For regeneration, only update the description
      instructions = goalName
        ? `Focus on generating a goal description for "${goalName}".`
        : 'Generate a goal description based on the brand context.';

      if (customInstructions && existingGoal) {
        // For regeneration, include the user's specific instructions
        instructions += `\n\nThe user has requested the following changes to the existing goal description: ${customInstructions}`;
      }
    }

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedGoal = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Goal') {
          generatedGoal = response;
        }
        originalUpdate(key, response);
      };

      // Store existing goal in content studio if regenerating
      if (existingGoal) {
        this.updateGeneratedContent(
          'Content Writer | Generate Goal',
          existingGoal,
        );
      }

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.missionStatement) {
          brandContext += `Mission Statement: ${brandKitData.missionStatement}\n`;
        }
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.keyMetrics) {
          brandContext += `Key Metrics: ${brandKitData.keyMetrics}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
        if (brandKitData.existingGoals) {
          brandContext += `\nOther Defined Goals:\n${brandKitData.existingGoals}\n`;
        }
        if (brandKitData.existingAudiences) {
          brandContext += `\nTarget Audiences:\n${brandKitData.existingAudiences}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Goal',
        type: existingGoal ? 'regenerate' : 'new',
        customInstructions: instructions,
        useSearch: true,
        revision:
          customInstructions && existingGoal ? customInstructions : undefined,
        setLoading: (loading: boolean) => {
          if (!loading && generatedGoal) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            // Try to parse JSON response for new goals
            if (generateTitle) {
              try {
                const parsed = JSON.parse(generatedGoal);
                if (parsed.title && parsed.description) {
                  resolve(parsed);
                } else {
                  resolve({ description: generatedGoal });
                }
              } catch {
                resolve({ description: generatedGoal });
              }
            } else {
              resolve(generatedGoal);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateMultipleAudiences(count: number = 4): Promise<
    {
      title: string;
      description: string;
    }[]
  > {
    // Create instructions for generating multiple diverse audiences
    const instructions = `Generate ${count} distinct target audience segments for the organization.

Return a JSON array where each audience has:
- "title": A concise, descriptive title (2-5 words)
- "description": A detailed 2-3 paragraph description

Make each audience distinct and complementary, covering different market segments, demographics, or needs.

Example format:
[
  {
    "title": "Young Professionals",
    "description": "Detailed description..."
  },
  {
    "title": "Established Families",
    "description": "Detailed description..."
  }
]`;

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Audience') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.missionStatement) {
          brandContext += `Mission Statement: ${brandKitData.missionStatement}\n`;
        }
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.keyMetrics) {
          brandContext += `Key Metrics: ${brandKitData.keyMetrics}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Audience',
        type: 'new',
        customInstructions: instructions,
        useSearch: true,
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              const audiences = JSON.parse(generatedResponse);
              if (Array.isArray(audiences)) {
                resolve(audiences);
              } else {
                resolve([]);
              }
            } catch {
              // Try to extract from formatted text if JSON parsing fails
              resolve([]);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async generateMultipleGoals(count: number = 4): Promise<
    {
      title: string;
      description: string;
    }[]
  > {
    // Create instructions for generating multiple diverse goals
    const instructions = `Generate ${count} distinct business goals for the organization.

Return a JSON array where each goal has:
- "title": A concise, descriptive title (2-5 words)
- "description": A detailed 2-3 paragraph description with strategies and metrics

Make each goal distinct and strategic, covering different aspects of business growth and success.

Example format:
[
  {
    "title": "Digital Transformation",
    "description": "Detailed description with strategies..."
  },
  {
    "title": "Market Expansion",
    "description": "Detailed description with strategies..."
  }
]`;

    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override the updateGeneratedContent method temporarily to capture the response
      const originalUpdate = this.updateGeneratedContent.bind(this);
      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Generate Goal') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Get brand kit data
      const brandKitData = this.getBrandKitData();

      // Build context from brand kit
      let brandContext = '';
      if (brandKitData) {
        brandContext = `\n\nCompany Context:\n`;
        if (brandKitData.missionStatement) {
          brandContext += `Mission Statement: ${brandKitData.missionStatement}\n`;
        }
        if (brandKitData.brandVoice) {
          brandContext += `Brand Voice: ${brandKitData.brandVoice}\n`;
        }
        if (brandKitData.keyMetrics) {
          brandContext += `Key Metrics: ${brandKitData.keyMetrics}\n`;
        }
        if (brandKitData.organizationWebsite) {
          brandContext += `Website: ${brandKitData.organizationWebsite}\n`;
        }
      }

      // Create a mock transcript that contains the brand context
      const brandKitTranscript =
        brandContext || 'No brand kit information available.';

      // Override the getTranscript method temporarily to return brand kit data
      const originalGetTranscript = this.getTranscript.bind(this);
      this.getTranscript = () => brandKitTranscript;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Generate Goal',
        type: 'new',
        customInstructions: instructions,
        useSearch: true,
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              const goals = JSON.parse(generatedResponse);
              if (Array.isArray(goals)) {
                resolve(goals);
              } else {
                resolve([]);
              }
            } catch {
              // Try to extract from formatted text if JSON parsing fails
              resolve([]);
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        reject(error);
      });
    });
  }

  public async identifySpeakersWithFrameAnalysis({
    selectedSpeakers,
    frameAnalysisResults,
  }: {
    selectedSpeakers?: Speaker[];
    frameAnalysisResults?: FrameAnalysisResult[];
  }): Promise<
    {
      speakerLabel: string;
      name: string;
      title: string;
    }[]
  > {
    // Build frame analysis context
    this.logger.info(
      'Frame analysis results for context:',
      frameAnalysisResults,
    );
    const frameAnalysisContext = frameAnalysisResults
      ?.filter(r => r.hasLowerThird && r.confidence !== 'low')
      .map(r => {
        // Convert spk_0 to Speaker 1 format for the prompt
        const speakerNumber = parseInt(r.speakerLabel.replace('spk_', '')) + 1;
        const parts = [`Speaker ${speakerNumber}`];
        if (r.name) parts.push(`Name: ${r.name}`);
        if (r.title) parts.push(`Title: ${r.title}`);
        if (r.company) parts.push(`Company: ${r.company}`);
        return parts.join(' - ');
      })
      .join('\n');

    this.logger.info('Frame analysis context built:', frameAnalysisContext);

    // Add frame analysis as additional context
    const additionalContext = frameAnalysisContext
      ? `\n\nVisual identification from video frames:\n${frameAnalysisContext}\n\nIMPORTANT: Use this visual information to identify the speakers. Match the Speaker numbers from the visual identification to the corresponding speakers in the transcript.`
      : '';

    // Call the improved chunked method with additional context
    return this.identifySpeakers({
      selectedSpeakers,
      additionalContext,
    });
  }

  public async identifySpeakers({
    selectedSpeakers,
    additionalContext = '',
  }: {
    selectedSpeakers?: Speaker[];
    additionalContext?: string;
  }): Promise<
    {
      speakerLabel: string;
      name: string;
      title: string;
    }[]
  > {
    // Get the transcript text
    const fullTranscript = this.getTranscript(
      'Content Writer | Identify Speaker Names' as SupportedGeneratedContentTypes,
      selectedSpeakers,
    );

    if (!fullTranscript || fullTranscript.length === 0) {
      this.logger.error('No transcript available for speaker identification');
      return [];
    }

    // Approximate token calculation: 1 token ~ 4 characters
    const TOKEN_LIMIT = 1_000_000;
    const AVG_CHARS_PER_TOKEN = 4;
    const SAFETY_MARGIN = 0.9; // 90% of max
    const MAX_CHARS = TOKEN_LIMIT * AVG_CHARS_PER_TOKEN * SAFETY_MARGIN;

    if (fullTranscript.length <= MAX_CHARS) {
      // For shorter transcripts, use the original single-pass approach
      this.logger.info(
        `Processing short transcript (${fullTranscript.length} chars) in single pass...`,
      );
      return this.identifySpeakersInChunk(
        fullTranscript,
        selectedSpeakers,
        additionalContext,
      );
    }

    // VERY UNRELIABLE, MODEL DOESN'T SEEM TO BENEFIT FROM SMALLER CONTEXT

    // Check if transcript is long enough to require chunking
    const TARGET_CHUNK_SIZE = 8000; // Target characters per chunk (approx 2000 tokens)
    const MAX_CHUNKS = 20; // Safety limit to prevent infinite loops

    this.logger.info(
      `Processing long transcript (${fullTranscript.length} chars) in chunks...`,
    );

    const speakerSegments = extractSpeakerSegments(fullTranscript);
    if (speakerSegments.length === 0) {
      return this.identifySpeakersInChunk(
        fullTranscript,
        selectedSpeakers,
        additionalContext,
      );
    }

    const BASE_OCCURRENCE_WEIGHT = 1;
    const ANCHOR_SEED_WEIGHT = 3;
    const EARLY_CHUNK_BOOST = 1.5; // applied to first third of chunks
    const VISUAL_CONTEXT_BOOST = 1.25;
    const hasVisualContext = /Visual identification from video frames:/i.test(
      additionalContext,
    );

    // Phase 1 - Seed Roster / Anchor Establishment
    const anchorMap = hasVisualContext
      ? new Map()
      : await this.seedSpeakerAnchors({
          fullTranscript,
          speakerSegments,
          selectedSpeakers,
          additionalContext,
          anchorSeedWeight: ANCHOR_SEED_WEIGHT,
        });

    // Build overlapping chunks (extracted method)
    const chunks = buildOverlappingChunks(speakerSegments, {
      targetChunkSize: TARGET_CHUNK_SIZE,
      maxChunks: MAX_CHUNKS,
      speakersPerChunk: 10,
      overlapSpeakers: 1,
      minChunkChars: 100,
      minNonFinalChunkChars: 500,
      createThirdPersonSupplementalChunks: !hasVisualContext,
    });

    // Process each chunk (after seed roster). Start with anchors pre-populated.
    const allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    > = new Map(anchorMap);

    for (let i = 0; i < chunks.length; i++) {
      if (chunks[i].length >= TARGET_CHUNK_SIZE * 2) {
        this.logger.warn(
          `Chunk ${i + 1} is very large (${
            chunks[i].length
          } chars), which may cause issues.`,
        );
      } else {
        this.logger.info(`Processing chunk ${i + 1}/${chunks.length}...`);
      }

      try {
        const chunkResults = await this.identifySpeakersInChunk(
          chunks[i],
          selectedSpeakers,
          additionalContext +
            `\n\nNote: This is chunk ${i + 1} of ${
              chunks.length
            } from a longer transcript.`,
        );

        const earlyChunkThreshold = Math.ceil(chunks.length / 3);
        const isEarlyChunk = i < earlyChunkThreshold;
        const chunkOccurrenceWeight =
          BASE_OCCURRENCE_WEIGHT * (isEarlyChunk ? EARLY_CHUNK_BOOST : 1);
        const visualMultiplier = hasVisualContext ? VISUAL_CONTEXT_BOOST : 1;

        this.mergeChunkResults({
          chunkResults,
          allIdentifiedSpeakers,
          chunkOccurrenceWeight,
          visualMultiplier,
        });
      } catch (error) {
        this.logger.error(`Error processing chunk ${i + 1}:`, error);
        // Continue with other chunks even if one fails
      }
    }

    const { finalResults, confidenceSummary } = this.finalizeSpeakerResults(
      allIdentifiedSpeakers,
    );
    this.logger.info(
      `Final speaker identification: ${finalResults.length} speakers | confidences ${confidenceSummary}`,
    );
    return finalResults;
  }

  private async identifySpeakersInChunk(
    transcriptChunk: string,
    selectedSpeakers?: Speaker[],
    additionalContext: string = '',
  ): Promise<{ speakerLabel: string; name: string; title: string }[]> {
    // Create a promise to collect the streamed response
    return new Promise((resolve, reject) => {
      let generatedResponse = '';

      // Override methods temporarily
      const originalUpdate = this.updateGeneratedContent.bind(this);
      const originalGetTranscript = this.getTranscript.bind(this);

      this.updateGeneratedContent = (
        key: SupportedGeneratedContentTypes,
        response: string = '',
      ) => {
        if (key === 'Content Writer | Identify Speaker Names') {
          generatedResponse = response;
        }
        originalUpdate(key, response);
      };

      // Override getTranscript to return the chunk
      this.getTranscript = () => transcriptChunk;

      // Use the existing regenerateStreamResponse method
      this.regenerateStreamResponse({
        key: 'Content Writer | Identify Speaker Names' as SupportedGeneratedContentTypes,
        type: 'new',
        customInstructions: additionalContext,
        useSearch: false,
        responseFormat: 'speaker_id',
        useBetterReasoning: true,
        selectedSpeakers, // Pass speakers for formatting
        setLoading: (loading: boolean) => {
          if (!loading && generatedResponse) {
            // Restore original methods
            this.updateGeneratedContent = originalUpdate;
            this.getTranscript = originalGetTranscript;

            try {
              // Clean the response before parsing
              let cleanedResponse = generatedResponse.trim();

              // Remove any markdown code blocks if present
              cleanedResponse = cleanedResponse.replace(
                /^```(?:json)?\s*/i,
                '',
              );
              cleanedResponse = cleanedResponse.replace(/\s*```$/i, '');

              // Remove any leading/trailing whitespace again
              cleanedResponse = cleanedResponse.trim();

              // Try to extract JSON from the response
              let identifiedSpeakers;
              try {
                identifiedSpeakers = JSON.parse(cleanedResponse);
              } catch (e) {
                // If direct parsing fails, try to find JSON array in the response
                const jsonMatch = cleanedResponse.match(
                  /\[\s*\{[\s\S]*?\}\s*\]/,
                );
                if (jsonMatch) {
                  identifiedSpeakers = JSON.parse(jsonMatch[0]);
                } else {
                  // As a last resort, check if the response got truncated
                  if (
                    cleanedResponse.includes('[') &&
                    !cleanedResponse.includes(']')
                  ) {
                    this.logger.error('Response appears to be truncated');
                    throw new Error('Response was truncated - incomplete JSON');
                  }
                  throw new Error('No valid JSON array found in response');
                }
              }

              if (
                !Array.isArray(identifiedSpeakers) &&
                identifiedSpeakers?.speakers
              ) {
                identifiedSpeakers = identifiedSpeakers.speakers;
              }

              // Validate the response structure
              if (Array.isArray(identifiedSpeakers)) {
                const validSpeakers = identifiedSpeakers.filter(
                  speaker =>
                    speaker.speakerLabel &&
                    speaker.name &&
                    speaker.title &&
                    typeof speaker.speakerLabel === 'string' &&
                    typeof speaker.name === 'string' &&
                    typeof speaker.title === 'string' &&
                    speaker.speakerLabel.startsWith('spk_'),
                );

                resolve(validSpeakers);
              } else {
                this.logger.error(
                  'Response is not an array:',
                  identifiedSpeakers,
                );
                resolve([]);
              }
            } catch (error) {
              this.logger.error(
                'Error parsing speaker identification response:',
                error,
              );
              this.logger.error('Raw response:', generatedResponse);

              // Try to parse the text format as a fallback
              try {
                const speakers =
                  this.parseTextFormatSpeakers(generatedResponse);
                resolve(speakers);
              } catch (fallbackError) {
                this.logger.error(
                  'Fallback parsing also failed:',
                  fallbackError,
                );
                resolve([]);
              }
            }
          }
        },
      }).catch(error => {
        // Restore original methods
        this.updateGeneratedContent = originalUpdate;
        this.getTranscript = originalGetTranscript;
        this.logger.error('Error identifying speakers:', error);
        resolve([]); // Return empty array on error rather than rejecting
      });
    });
  }

  // Helper method to parse text format responses
  private parseTextFormatSpeakers(text: string): {
    speakerLabel: string;
    name: string;
    title: string;
  }[] {
    const speakers: any[] = [];

    // Split by numbered items
    const lines = text.split('\n');
    let currentSpeaker: any = null;
    let speakerIndex = 0;

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Check for numbered speaker entries (e.g., "1. **Name**" or "1. Name")
      const nameMatch = trimmedLine.match(/^\d+\.\s+\*{0,2}(.+?)\*{0,2}$/);
      if (nameMatch) {
        if (currentSpeaker && currentSpeaker.name) {
          speakers.push(currentSpeaker);
        }
        currentSpeaker = {
          speakerLabel: `spk_${speakerIndex}`,
          name: nameMatch[1].trim(),
          title: '',
        };
        speakerIndex++;
      }

      // Check for title lines (e.g., "- **Title:** Company | Position")
      const titleMatch = trimmedLine.match(
        /[-•]\s+\*{0,2}Title:\*{0,2}\s+(.+)$/i,
      );
      if (titleMatch && currentSpeaker) {
        currentSpeaker.title = titleMatch[1].trim();
      }
    }

    // Add the last speaker if exists
    if (currentSpeaker && currentSpeaker.name && currentSpeaker.title) {
      speakers.push(currentSpeaker);
    }

    return speakers.filter(s => s.name && s.title);
  }

  /**
   * Seed speaker anchors by constructing a diversity-first roster slice of the transcript
   * prioritizing: self-introductions > third-person introductions > chronological order.
   * Each unique speaker contributes a truncated snippet to a roster chunk which is used
   * for an initial identification pass. Results are converted into an anchor map with
   * elevated confidence/weight (ANCHOR_SEED_WEIGHT) to stabilize subsequent chunk merging.
   */
  private async seedSpeakerAnchors(params: {
    fullTranscript: string;
    speakerSegments: { speaker: string; content: string }[];
    selectedSpeakers?: Speaker[];
    additionalContext: string;
    anchorSeedWeight: number;
  }): Promise<
    Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >
  > {
    const {
      fullTranscript,
      speakerSegments,
      selectedSpeakers,
      additionalContext,
      anchorSeedWeight,
    } = params;
    const rosterText = buildRosterTextForAnchors(
      speakerSegments,
      fullTranscript,
    );
    const anchorMap: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    > = new Map();
    try {
      const anchorResults = await this.identifySpeakersInChunk(
        rosterText,
        selectedSpeakers,
        additionalContext +
          '\n\nThis is an initial seed roster chunk. Produce provisional mappings. If uncertain use placeholder like "Unknown".',
      );
      anchorResults.forEach(r => {
        anchorMap.set(r.speakerLabel, {
          name: r.name,
          title: r.title,
          confidence: anchorSeedWeight,
          nameCounts: r.name
            ? { [this.normalizeName(r.name)]: anchorSeedWeight }
            : {},
          titleCounts: r.title
            ? { [this.normalizeName(r.title)]: anchorSeedWeight }
            : {},
        });
      });
      this.logger.info(
        `Anchors established: ${anchorMap.size} provisional speaker identities`,
      );
    } catch (e) {
      this.logger.warn(
        'Seed roster identification failed; proceeding without anchors',
        e,
      );
    }
    return anchorMap;
  }

  /**
   * Merge chunk-level identification results into the aggregate map with
   * weighting & inline upgrade heuristics.
   */
  private mergeChunkResults(params: {
    chunkResults: { speakerLabel: string; name: string; title: string }[];
    allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >;
    chunkOccurrenceWeight: number;
    visualMultiplier: number;
  }) {
    const {
      chunkResults,
      allIdentifiedSpeakers,
      chunkOccurrenceWeight,
      visualMultiplier,
    } = params;

    chunkResults.forEach(speaker => {
      const existing = allIdentifiedSpeakers.get(speaker.speakerLabel);
      if (!existing) {
        allIdentifiedSpeakers.set(speaker.speakerLabel, {
          name: speaker.name,
          title: speaker.title,
          confidence: chunkOccurrenceWeight * visualMultiplier,
          nameCounts: speaker.name
            ? {
                [this.normalizeName(speaker.name)]:
                  chunkOccurrenceWeight * visualMultiplier,
              }
            : {},
          titleCounts: speaker.title
            ? {
                [this.normalizeName(speaker.title)]:
                  chunkOccurrenceWeight * visualMultiplier,
              }
            : {},
        });
        return;
      }
      // Existing speaker label encountered again
      const weighted = chunkOccurrenceWeight * visualMultiplier;
      existing.confidence += weighted;
      const normalizedIncomingName = this.normalizeName(speaker.name || '');
      if (normalizedIncomingName) {
        existing.nameCounts[normalizedIncomingName] =
          (existing.nameCounts[normalizedIncomingName] || 0) + weighted;
        if (this.shouldReplaceInline(existing.name, normalizedIncomingName)) {
          existing.name = normalizedIncomingName;
        }
      }
      const normalizedIncomingTitle = this.normalizeName(speaker.title || '');
      if (normalizedIncomingTitle) {
        existing.titleCounts[normalizedIncomingTitle] =
          (existing.titleCounts[normalizedIncomingTitle] || 0) + weighted;
        // Inline replace only if existing title empty or candidate extends (adds tokens)
        if (
          !existing.title ||
          this.isExtension(existing.title, normalizedIncomingTitle)
        ) {
          existing.title = normalizedIncomingTitle;
        }
      }
    });
  }

  /**
   * Finalize canonical name/title selection and build confidence summary from the aggregate map.
   */
  private finalizeSpeakerResults(
    allIdentifiedSpeakers: Map<
      string,
      {
        name: string;
        title: string;
        confidence: number;
        nameCounts: Record<string, number>;
        titleCounts: Record<string, number>;
      }
    >,
  ): {
    finalResults: { speakerLabel: string; name: string; title: string }[];
    confidenceSummary: string;
  } {
    const finalResults = Array.from(allIdentifiedSpeakers.entries()).map(
      ([speakerLabel, data]) => {
        const canonicalName = this.selectCanonical(
          data.nameCounts,
          this.normalizeName(data.name),
        );
        const canonicalTitle = this.selectCanonical(
          data.titleCounts,
          this.normalizeName(data.title),
        );
        return { speakerLabel, name: canonicalName, title: canonicalTitle };
      },
    );
    const confidenceSummary = Array.from(allIdentifiedSpeakers.entries())
      .map(([label, d]) => `${label}:${d.confidence.toFixed(1)}`)
      .join(' ');
    return { finalResults, confidenceSummary };
  }

  // ---------------------------------------------------------------------------
  // Identity Heuristic Helper Methods (extracted from identifySpeakers)
  // ---------------------------------------------------------------------------
  /** Normalize a name/title string for stable comparison & counting. */
  private normalizeName(raw: string | undefined | null): string {
    if (!raw) return '';
    let s = raw
      .replace(/^["'`\-\s]+|["'`\-\s]+$/g, '') // trim quotes / dashes
      .replace(/\s+/g, ' ') // collapse whitespace
      .trim();
    // Guard against pathological length (avoid huge hallucinations skewing counts)
    if (s.length > 120) s = s.slice(0, 120);
    return s;
  }

  /** Generic/placeholder surface forms that shouldn't override a specific identity. */
  private isGenericName(name: string): boolean {
    const n = name.toLowerCase();
    if (!n) return true;
    return (
      n === 'unknown' ||
      n === 'speaker' ||
      /^speaker \d+$/.test(n) ||
      /^spk[_-]?\d+$/.test(n) ||
      ['host', 'moderator', 'guest', 'narrator', 'announcer'].includes(n)
    );
  }

  /** Tokenize a surface (letters + numbers) for extension checks & specificity scoring. */
  private tokenize(str: string): string[] {
    if (!str) return [];
    return str
      .split(/[^A-Za-z0-9]+/)
      .map(t => t.trim())
      .filter(Boolean);
  }

  /**
   * Returns true if candidate is a simple extension of existing (e.g. "Jane" -> "Jane Doe").
   * All existing tokens must appear in order at the start of candidate and candidate has > tokens.
   */
  private isExtension(existing: string, candidate: string): boolean {
    const a = this.tokenize(this.normalizeName(existing).toLowerCase());
    const b = this.tokenize(this.normalizeName(candidate).toLowerCase());
    if (a.length === 0 || b.length === 0) return false;
    if (b.length <= a.length) return false;
    return a.every((tok, idx) => b[idx] === tok);
  }

  /** Decide whether to inline replace an existing name with an incoming candidate during streaming merge. */
  private shouldReplaceInline(existing: string, incoming: string): boolean {
    if (!existing) return !!incoming;
    if (!incoming) return false;
    const normalizedExisting = this.normalizeName(existing);
    const normalizedIncoming = this.normalizeName(incoming);
    if (!normalizedIncoming) return false;
    if (normalizedExisting === normalizedIncoming) return false;
    const existingGeneric = this.isGenericName(normalizedExisting);
    const incomingGeneric = this.isGenericName(normalizedIncoming);
    // Upgrade from generic -> specific
    if (existingGeneric && !incomingGeneric) return true;
    // Never downgrade specific -> generic
    if (!existingGeneric && incomingGeneric) return false;
    // Allow simple extensions (adds surname etc.)
    if (this.isExtension(normalizedExisting, normalizedIncoming)) return true;
    return false;
  }

  /**
   * Select a canonical surface from weighted occurrence counts.
   * Preference order: highest weight (non-generic) -> max token count -> longest length.
   */
  private selectCanonical(
    counts: Record<string, number>,
    current?: string,
  ): string {
    const entries = Object.entries(counts);
    if (entries.length === 0) return current || '';
    // Separate generic vs specific to prioritize specific forms.
    const scored = entries.map(([name, weight]) => {
      const norm = this.normalizeName(name);
      const tokens = this.tokenize(norm);
      return {
        name: norm,
        weight,
        tokens,
        tokenCount: tokens.length,
        isGeneric: this.isGenericName(norm),
        length: norm.length,
      };
    });
    scored.sort((a, b) => {
      // Non-generic before generic
      if (a.isGeneric !== b.isGeneric) return a.isGeneric ? 1 : -1;
      // Higher weight
      if (b.weight !== a.weight) return b.weight - a.weight;
      // More tokens (greater specificity)
      if (b.tokenCount !== a.tokenCount) return b.tokenCount - a.tokenCount;
      // Longer length as final tiebreaker
      if (b.length !== a.length) return b.length - a.length;
      return a.name.localeCompare(b.name);
    });
    return scored[0].name;
  }
}
